/**
 * Copyright © 2026 Adnuntius AS.
 */
import {AfterContentInit, ContentChildren, Directive, ElementRef, NgZone, OnDestroy, QueryList} from '@angular/core';
import {NgModel} from '@angular/forms';
import {merge, Subscription} from 'rxjs';

@Directive({
  standalone: true,
  selector: '[adnAdminFieldGroup]'
})
export class AdminFieldGroupDirective implements AfterContentInit, OnDestroy {
  @ContentChildren(NgModel, {descendants: true}) ngModels: QueryList<NgModel>;

  private subscription: Subscription;
  private helpBlocks: HTMLElement[] = [];

  constructor(
    private element: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {
  }

  ngAfterContentInit(): void {
    this.subscribeToModels();
    this.ngModels.changes.subscribe(() => this.subscribeToModels());
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.clearHelpBlocks();
  }

  private subscribeToModels(): void {
    this.subscription?.unsubscribe();
    this.sync();
    if (this.ngModels.length) {
      this.subscription = merge(...this.ngModels.map((model) => model.statusChanges)).subscribe(() => {
        this.ngZone.run(() => this.sync());
      });
    }
  }

  private sync(): void {
    const invalid = this.ngModels.some((model) => model.invalid);
    const warningMessages = this.ngModels
      .map((model) => (model.control as any)?.validation)
      .filter((message): message is string => !!message);
    const hasWarning = warningMessages.length > 0;

    this.element.nativeElement.classList.toggle('has-error', invalid);
    this.element.nativeElement.classList.toggle('has-warning', hasWarning);
    this.renderWarningHelpBlocks(warningMessages);
  }

  private clearHelpBlocks(): void {
    this.helpBlocks.forEach((el) => el.remove());
    this.helpBlocks = [];
  }

  private renderWarningHelpBlocks(messages: string[]): void {
    this.clearHelpBlocks();
    if (!messages.length) {
      return;
    }

    const host = this.element.nativeElement.querySelector('.col-md-9, .col-sm-9, .col-md-5, .col-sm-5')
      || this.element.nativeElement;

    // Dedupe in case multiple ngModels share the same API warning text.
    Array.from(new Set(messages)).forEach((message) => {
      const help = document.createElement('div');
      help.className = 'help-block';
      help.textContent = message;
      host.appendChild(help);
      this.helpBlocks.push(help);
    });
  }
}
